import * as React from 'react'

import { MobXProviderContext, observer } from 'mobx-react'

import { LocationType } from '~/client/graph'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import { useEquipmentResourceStore } from '~/client/src/shared/hooks/useEquipmentResourceStore'
import { useLocationResourceStore } from '~/client/src/shared/hooks/useLocationResourceStore'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import type LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import type InitialState from '~/client/src/shared/stores/InitialState'

import ResourcePicker from '../ResourcePicker/ResourcePicker'
import { withKeepSelected } from '../ResourcePicker/resourceWorkflowVisibility'
import { useResourcePickerCards } from '../ResourcePicker/useResourceRenderItem'

interface IProps {
  mode: 'single' | 'multi'
  selectedIds: string[]
  onSelect: (ids: string[]) => void
  onClose: () => void
  /** Hidden unless selected. */
  excludedLocationTypes?: readonly LocationType[]
  stageTreeSelection?: boolean
  allowEmptySelection?: boolean
}

const NO_RECORDS: IResourceRecord[] = []

const LocationEquipmentResourcePicker: React.FC<IProps> = ({
  mode,
  selectedIds,
  onSelect,
  onClose,
  excludedLocationTypes,
  stageTreeSelection,
  allowEmptySelection,
}) => {
  const { state, locationAttributesStore } = React.useContext(
    MobXProviderContext,
  ) as { state: InitialState; locationAttributesStore: LocationAttributesStore }
  const projectId = state.activeProject?.id
  const locations = useLocationResourceStore(projectId)
  const equipment = useEquipmentResourceStore(projectId)

  // TODO(STX1-680): drop once picked ids display from location resources. Until then they
  // display through the legacy kind stores, which also give the kind to exclude by. Read
  // through a ref: the map is rebuilt on any location change, which already re-sends the
  // records.
  const legacyByIdRef = React.useRef(
    locationAttributesStore.hierarchyAllAttributesMap,
  )
  legacyByIdRef.current = locationAttributesStore.hierarchyAllAttributesMap

  const locationCards = useResourcePickerCards(
    ResourceTypeId.LOCATIONS,
    locations.schema,
  )
  const equipmentCards = useResourcePickerCards(
    ResourceTypeId.OFFLOADING_EQUIPMENT,
    equipment.schema,
  )

  // The picker validates its grouping and sort against the fields both types bring. Once
  // in, a later schema refresh must not remount it and drop a multi pick in progress.
  const readyRef = React.useRef(false)
  readyRef.current ||=
    !locations.loading &&
    !equipment.loading &&
    locationCards.ready &&
    equipmentCards.ready
  const ready = readyRef.current

  // An id with no record (an integration) stays selected, unseen, so an untouched Apply
  // keeps it; picking replaces it.
  const selectedKey = selectedIds.join(',')

  const excludedKey = excludedLocationTypes?.join(',')
  const recordPredicate = React.useMemo(() => {
    const excluded = new Set(excludedLocationTypes)
    return withKeepSelected((record: IResourceRecord) => {
      const legacy = legacyByIdRef.current[record.id]
      return !!legacy && !excluded.has(legacy.type)
    }, selectedIds)
  }, [excludedKey, selectedKey])

  const resourceTypes = React.useMemo(
    () =>
      [
        {
          resourceTypeId: ResourceTypeId.LOCATIONS,
          label: Localization.translator.locations,
          records: ready ? locations.records : NO_RECORDS,
          properties: locationCards.properties,
          resourceSchema: locations.schema,
          renderItem: locationCards.renderItem,
        },
        {
          resourceTypeId: ResourceTypeId.OFFLOADING_EQUIPMENT,
          label: Localization.translator.equipment,
          records: ready ? equipment.records : NO_RECORDS,
          properties: equipmentCards.properties,
          resourceSchema: equipment.schema,
          renderItem: equipmentCards.renderItem,
        },
      ] as const,
    [
      ready,
      locations.records,
      locations.schema,
      locationCards.properties,
      locationCards.renderItem,
      equipment.records,
      equipment.schema,
      equipmentCards.properties,
      equipmentCards.renderItem,
    ],
  )

  return (
    <ResourcePicker
      // The picker seeds its selection, grouping and sort once: remount when the data
      // lands and when the host's value changes under it, so a host must not change
      // the value while the picker is open.
      key={`${ready}|${selectedKey}`}
      resourceTypes={resourceTypes}
      mode={mode}
      selectedIds={selectedIds}
      onSelect={onSelect}
      loading={!ready}
      propertiesReady={
        locationCards.propertiesReady && equipmentCards.propertiesReady
      }
      recordPredicate={recordPredicate}
      stageTreeSelection={stageTreeSelection}
      allowEmptySelection={allowEmptySelection}
    >
      <ResourcePicker.Toolbar />
      <ResourcePicker.List countLabel={Localization.translator.resources} />
      <ResourcePicker.EmptyState />
      <ResourcePicker.Footer onCancel={onClose} />
    </ResourcePicker>
  )
}

export default observer(LocationEquipmentResourcePicker)
